import React, { useEffect, useState } from 'react'
import { MarketActivityIndicator } from '@market/react'
import { buildTranslate } from 'locales'
import { loyaltyApi } from 'api'
import { useGetCustomer } from 'hooks/customer'
import { RequestContext } from 'utils/pos'
import { useHistory } from 'react-router-dom'
import { Discount, order as squareOrderApi } from '@squareup/sq-addons-web-sdk'
import ConnectionError from 'components/ConnectionError'
import { getErrorMessage, getErrorStatus } from 'utils/api'
import {
  convertRedeemedOfferToDiscount,
  getOrderDiscount,
  hasDiscountAmountChanged,
  updateOrderDiscount,
} from 'utils/loyalty'

const t = buildTranslate('offerValidation')

const ValidateOfferScreen = () => {
  const history = useHistory()
  const [validateOffer] = loyaltyApi().useValidateOfferMutation()
  const { getCustomer } = useGetCustomer()
  const [isError, setIsError] = useState(false)

  async function validate() {
    setIsError(false)
    const [customerResult, order, discount] = await Promise.all([
      getCustomer(RequestContext.SDK),
      squareOrderApi.retrieve(),
      getOrderDiscount(),
    ])

    // if there's no customer in the order, go to the main screen
    if (customerResult.isErr()) {
      history.push('/' + window.location.search)
      return
    }

    const customer = customerResult.value

    const redemptionIdentifier = discount?.metadata?.redemptionId ?? null

    // if there is no applied discount, go to the customer screen
    if (!redemptionIdentifier || !discount?.uid) {
      history.push('/customer' + window.location.search)
      return
    }

    const validateOrderResult = await validateOffer({
      userIdentifier: customer.referenceId,
      order,
      redemptionIdentifier,
    })

    if (validateOrderResult.error) {
      if (getErrorStatus(validateOrderResult.error) === 400) {
        const updatedDiscount = {
          ...discount,
          uid: discount.uid,
          metadata: {
            ...discount.metadata,
            errorMessage: getErrorMessage(validateOrderResult.error),
          },
        } as unknown as Discount
        await updateOrderDiscount(discount.uid, updatedDiscount)
        history.push('/invalid-discounts' + window.location.search)
        return
      }

      setIsError(true)
      return
    }

    const appliedOffer = validateOrderResult.data.offer
    if (hasDiscountAmountChanged(discount, appliedOffer)) {
      await updateOrderDiscount(
        discount.uid,
        convertRedeemedOfferToDiscount(appliedOffer)
      )
    }

    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set('offer', 'valid')

    history.push(`/customer?${searchParams.toString()}`)
  }

  useEffect(() => {
    validate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isError) {
    return <ConnectionError onClickTryAgain={validate} />
  }

  return (
    <div className="market-grid-container" data-testid="validate-offer-screen">
      <div
        className="addons-checking-rewards-loading-indicator market-grid-item-full"
        data-testid="checking-rewards-component"
      >
        <MarketActivityIndicator data-testid="loading-indicator" size="small" />
        <h3>{t('title')}</h3>
        <p>{t('subtitle')}</p>
      </div>
    </div>
  )
}

export default ValidateOfferScreen
